// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState, useEffect } from "react";
import { Shield, FileText, Clock, Eye, Settings } from "lucide-react";
import gdprService from "../../services/gdprService";
import DashboardTab from "../../components/admin/gdpr/DashboardTab";
import DataManagementTab from "../../components/admin/gdpr/DataManagementTab";
import RetentionPoliciesTab from "../../components/admin/gdpr/RetentionPoliciesTab";
import AuditLogTab from "../../components/admin/gdpr/AuditLogTab";
import DataProtectionTab from "../../components/admin/gdpr/DataProtectionTab";
import GlobalAdminHeader from "../../components/admin/GlobalAdminHeader";
import PageLoader from "../../components/PageLoader";

const GDPRAdminInterface = () => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [dataRecords, setDataRecords] = useState([]);
  const [retentionPolicies, setRetentionPolicies] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [gdprStatus, setGdprStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Track which tabs have been loaded
  const [loadedTabs, setLoadedTabs] = useState(new Set());
  const tenantId = "8d98e352-365f-11f0-8dea-d515ea57f3bd";

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [statusData, auditData] = await Promise.all([
        gdprService.getGDPRStatus(tenantId),
        gdprService.getAuditLogs(tenantId),
      ]);

      setGdprStatus(statusData);

      // Transform audit data for dashboard
      if (auditData && Array.isArray(auditData) && auditData.length > 0) {
        const transformedAuditLogs = transformAuditLogs(auditData);
        setAuditLogs(transformedAuditLogs);
      } else {
        setAuditLogs([]);
      }

      setLoadedTabs((prev) => new Set([...prev, "dashboard", "audit-log"]));
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setError(
        "Fehler beim Laden der Dashboard-Daten. Bitte versuchen Sie es erneut.",
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle tab change with lazy loading
  const handleTabChange = async (tabId) => {
    setActiveTab(tabId);
    if (!loadedTabs.has(tabId)) {
      await loadTabData(tabId);
    }
  };

  // Load data for specific tab
  const loadTabData = async (tabId) => {
    setLoading(true);
    setError(null);

    try {
      switch (tabId) {
        case "dashboard":
          break;

        case "data-management":
          await loadDataManagementData();
          break;

        case "retention-policies":
          await loadRetentionPoliciesData();
          break;

        case "audit-log":
          await loadAuditLogData();
          break;

        case "data-protection":
          break;

        default:
          break;
      }

      setLoadedTabs((prev) => new Set([...prev, tabId]));
    } catch (error) {
      console.error(`Error loading ${tabId} data:`, error);
      setError(
        `Fehler beim Laden der ${tabId}-Daten. Bitte versuchen Sie es erneut.`,
      );
    } finally {
      setLoading(false);
    }
  };

  // Load data management data
  const loadDataManagementData = async () => {
    const recordsData = await gdprService.getDataRecords(
      tenantId,
      pageSize,
      currentPage,
    );
    setDataRecords(recordsData?.records || []);
    setTotalRecords(recordsData?.pagination?.total_records || 0);
    setTotalPages(recordsData?.pagination?.total_pages || 0);
  };

  // Load retention policies data
  const loadRetentionPoliciesData = async () => {
    const policiesData = await gdprService.getRetentionPolicies(tenantId);
    if (policiesData && Array.isArray(policiesData)) {
      const transformedPolicies = transformRetentionPolicies(policiesData);
      setRetentionPolicies(transformedPolicies);
    } else {
      setRetentionPolicies([]);
    }
  };

  // Load audit log data
  const loadAuditLogData = async () => {
    const auditData = await gdprService.getAuditLogs(tenantId);
    if (auditData && Array.isArray(auditData) && auditData.length > 0) {
      const transformedAuditLogs = transformAuditLogs(auditData);
      setAuditLogs(transformedAuditLogs);
    } else {
      setAuditLogs([]);
    }
  };

  // Fetch data records with pagination (only when data management tab is active)
  const fetchDataRecords = async (page = 1, size = 25) => {
    if (activeTab !== "data-management") return;

    try {
      const recordsData = await gdprService.getDataRecords(
        tenantId,
        size,
        page,
      );
      setDataRecords(recordsData?.records || []);
      setTotalRecords(recordsData?.pagination?.total_records || 0);
      setCurrentPage(page);
      setPageSize(recordsData?.pagination?.limit || size);
      setTotalPages(recordsData?.pagination?.total_pages || 0);
    } catch (error) {
      console.error("Error fetching data records:", error);
      setError(
        "Fehler beim Laden der Datensätze. Bitte versuchen Sie es erneut.",
      );
    }
  };

  // Refresh current tab data
  const refreshCurrentTab = async () => {
    if (activeTab === "dashboard") {
      await loadDashboardData();
    } else if (loadedTabs.has(activeTab)) {
      await loadTabData(activeTab);
    }
  };

  // Transform API audit logs to component format
  const transformAuditLogs = (apiAuditLogs) => {
    if (!Array.isArray(apiAuditLogs)) {
      return [];
    }

    return apiAuditLogs.map((log) => {
      try {
        let parsedNewState = null;
        let parsedPreviousState = null;

        if (log.new_state) {
          try {
            parsedNewState = JSON.parse(log.new_state);
          } catch (parseError) {
            console.warn(
              "Failed to parse new_state:",
              log.new_state,
              parseError,
            );
          }
        }

        if (log.previous_state) {
          try {
            parsedPreviousState = JSON.parse(log.previous_state);
          } catch (parseError) {
            console.warn(
              "Failed to parse previous_state:",
              log.previous_state,
              parseError,
            );
          }
        }

        return {
          id: log.log_id,
          timestamp: formatTimestamp(log.created_at),
          action: getActionDisplayName(log.action, log.entity_type),
          dataSubject: getDataSubject(log, parsedNewState),
          performedBy: getPerformedBy(log, parsedNewState),
          details: getActionDetails(log, parsedNewState),
          ipAddress: log.ip_address || "Nicht verfügbar",
          entityType: log.entity_type,
          entityId: log.entity_id,
          previousState: parsedPreviousState,
          newState: parsedNewState,
        };
      } catch (error) {
        console.error("Error transforming log entry:", log, error);
        return {
          id: log.log_id || "unknown",
          timestamp: "Fehler",
          action: "Fehler beim Laden",
          dataSubject: "Unbekannt",
          performedBy: "Unbekannt",
          details: "Fehler beim Verarbeiten der Daten",
          ipAddress: "Nicht verfügbar",
          entityType: log.entity_type || "unknown",
          entityId: log.entity_id || "unknown",
        };
      }
    });
  };

  // Transform API retention policies to component format
  const transformRetentionPolicies = (apiPolicies) => {
    if (!Array.isArray(apiPolicies)) {
      return [];
    }

    return apiPolicies.map((policy) => {
      try {
        return {
          id: policy.id,
          name: getPolicyDisplayName(policy.entity_type),
          dataType: policy.entity_type,
          retentionPeriod: Math.floor(policy.retention_days / 365),
          retentionUnit: "years",
          softDeletePeriod: Math.floor(policy.soft_delete_days / 365),
          hardDeletePeriod: Math.floor(policy.hard_delete_days / 365),
          autoDelete: policy.soft_delete_days > 0,
          legalBasis: policy.legal_basis,
          isActive: policy.is_active,
          createdAt: formatTimestamp(policy.created_at),
          rawPolicy: policy,
        };
      } catch (error) {
        console.error("Error transforming policy:", policy, error);
        return {
          id: policy.id || "unknown",
          name: "Fehler beim Laden",
          dataType: policy.entity_type || "unknown",
          retentionPeriod: 0,
          retentionUnit: "years",
          autoDelete: false,
          legalBasis: "Fehler beim Verarbeiten der Daten",
          isActive: false,
        };
      }
    });
  };

  // Helper functions
  const getActionDisplayName = (action, entityType) => {
    const actionMap = {
      create: "Erstellt",
      update: "Aktualisiert",
      delete: "Gelöscht",
      soft_delete: "Soft Delete",
      hard_delete: "Hard Delete",
      export: "Exportiert",
      login: "Anmeldung",
      logout: "Abmeldung",
    };

    const entityMap = {
      user: "Benutzer",
      tenant: "Mandant",
      booking: "Buchung",
      organization: "Organisation",
    };

    const actionText = actionMap[action] || action;
    const entityText = entityMap[entityType] || entityType;

    return `${actionText} ${entityText}`;
  };

  const getPolicyDisplayName = (entityType) => {
    const nameMap = {
      vhp_user: "Benutzerdaten Standard",
      vhp_booking: "Buchungsdaten Geschäftskunden",
      audit_logs: "Audit-Protokolle",
      workflow_execution_log: "Workflow-Ausführungsprotokolle",
      workflow_email_notifications: "Workflow-E-Mail-Benachrichtigungen",
    };

    return nameMap[entityType] || entityType;
  };

  const getDataSubject = (log, parsedNewState) => {
    if (log.entity_type === "user" && parsedNewState) {
      if (
        parsedNewState.first_name &&
        parsedNewState.last_name &&
        parsedNewState.email
      ) {
        return `${parsedNewState.first_name} ${parsedNewState.last_name} (${parsedNewState.email})`;
      } else if (parsedNewState.email) {
        return parsedNewState.email;
      } else if (parsedNewState.user_id) {
        return `Benutzer ${parsedNewState.user_id}`;
      }
    } else if (log.entity_type === "tenant" && parsedNewState) {
      if (parsedNewState.name) {
        return parsedNewState.name;
      } else if (parsedNewState.tenant_id) {
        return `Mandant ${parsedNewState.tenant_id}`;
      }
    }
    return `${log.entity_type} ${log.entity_id}`;
  };

  const getPerformedBy = (log, parsedNewState) => {
    if (log.user_id) {
      if (parsedNewState && parsedNewState.email) {
        return parsedNewState.email;
      }
      return `Benutzer ${log.user_id}`;
    }
    return "System";
  };

  const getActionDetails = (log, parsedNewState) => {
    if (log.action === "create" && parsedNewState) {
      if (log.entity_type === "user") {
        if (parsedNewState.email) {
          return `Neuer Benutzer erstellt: ${parsedNewState.email}`;
        } else if (parsedNewState.first_name && parsedNewState.last_name) {
          return `Neuer Benutzer erstellt: ${parsedNewState.first_name} ${parsedNewState.last_name}`;
        }
      } else if (log.entity_type === "tenant") {
        if (parsedNewState.name) {
          return `Neuer Mandant erstellt: ${parsedNewState.name}`;
        } else if (parsedNewState.subdomain) {
          return `Neuer Mandant erstellt: ${parsedNewState.subdomain}`;
        }
      }
    } else if (log.action === "update" && parsedNewState) {
      if (log.entity_type === "user" && parsedNewState.email) {
        return `Benutzer aktualisiert: ${parsedNewState.email}`;
      } else if (log.entity_type === "tenant" && parsedNewState.name) {
        return `Mandant aktualisiert: ${parsedNewState.name}`;
      }
    }
    return `${log.action} auf ${log.entity_type} ${log.entity_id}`;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "Unbekannt";
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        return timestamp;
      }
      return date.toLocaleString("de-DE");
    } catch (error) {
      return timestamp;
    }
  };

  const tabs = [
    { id: "dashboard", name: "Dashboard", icon: Shield },
    { id: "data-management", name: "Datenverwaltung", icon: FileText },
    {
      id: "retention-policies",
      name: "Aufbewahrungsrichtlinien",
      icon: Clock,
    },
    { id: "audit-log", name: "Audit-Protokoll", icon: Eye },
    { id: "data-protection", name: "Schutzmaßnahmen", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      {/* Header */}
      <GlobalAdminHeader
        title="Stadtplatz.de DSGVO Administration"
        subtitle="VHP2 Datenschutz-Management für Urban Space GmbH"
        buttons={[
          {
            text: `Letzter Login: ${new Date().toLocaleDateString("de-DE")}`,
            className:
              "text-sm text-gray-500 dark:text-slate-400 bg-transparent border-none cursor-default",
          },
        ]}
      />

      {loading && <PageLoader message="Lade Daten..." />}

      {/* Content */}
      {!loading && (
        <div className="mx-auto px-4 lg:px-4 py-4 text-left dark:text-slate-200">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200 dark:border-slate-700 mb-8">
            <nav className="-mb-px flex space-x-8 overflow-x-auto">
              {tabs.map((tab) => {
                const IconComponent = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 whitespace-nowrap ${
                      activeTab === tab.id
                        ? "border-primary-color primary-color"
                        : "border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 hover:border-gray-300 dark:hover:border-slate-600"
                    }`}
                  >
                    <IconComponent className="w-4 h-4" />
                    <span>{tab.name}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div>
            {activeTab === "dashboard" && (
              <DashboardTab
                gdprStatus={gdprStatus}
                auditLogs={auditLogs}
                error={error}
                onRefresh={refreshCurrentTab}
                loading={loading}
              />
            )}
            {activeTab === "data-management" && (
              <DataManagementTab
                dataRecords={dataRecords}
                currentPage={currentPage}
                pageSize={pageSize}
                totalRecords={totalRecords}
                totalPages={totalPages}
                onPageChange={fetchDataRecords}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                  fetchDataRecords(1, size);
                }}
              />
            )}
            {activeTab === "retention-policies" && (
              <RetentionPoliciesTab retentionPolicies={retentionPolicies} />
            )}
            {activeTab === "audit-log" && <AuditLogTab auditLogs={auditLogs} />}
            {activeTab === "data-protection" && <DataProtectionTab />}
          </div>
        </div>
      )}
    </div>
  );
};

export default GDPRAdminInterface;
